

import { firebaseService, getAuthToken } from './firebaseService';
import { Task, SubTask, SystemEvent, EmailLog, HopTrace, MessageTemplate } from '../types';
import { EVENT_REQUIRED_CONTEXT } from './templateContextRegistry';
import { API_BASE } from '../apiConfig';

/**
 * TACTICAL ENDPOINT: /api/relay
 * Uses API_BASE to support both web (relative) and mobile (absolute Cloud Run URL) builds.
 */
const RELAY_ENDPOINT = `${API_BASE}/api/relay`;

/**
 * Utility to wait for a specified duration (ms)
 */
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const communicationService = {
  /**
   * Diagnostic Utility: Verifies Postmark Integration
   * Checks library installation, environment variables, and outbound network connectivity.
   */
  async testPostmarkConnection(timeoutMs: number = 5000): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      console.log("[DIAG] Pinging /api/diagnostics/postmark...");
      const authToken = await getAuthToken();
      const response = await fetch(`${API_BASE}/api/diagnostics/postmark`, {
        method: 'GET',
        headers: { 'Accept': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        // Improved check: If response is 404 (File not found) or contains HTML-like content, assume backend not reachable
        if (text.trim().startsWith('<') || text.includes('404') || text.includes('Cannot GET')) {
          console.error("[DIAG_FAIL] Non-JSON response received.", text);
          throw new Error("Connection Error: Backend not reachable (404/500). Ensure server.js is running.");
        }
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        throw new Error(data.error || `Server returned ${response.status}`);
      }

      return data;
    } catch (e: any) {
      clearTimeout(timeoutId);
      console.error("[Postmark Diagnostic] Failed:", e);
      return {
        success: false,
        error: e.message || "Network Error",
        library_installed: false,
        outbound_network: false,
        env_var_present: false,
        details: "Frontend could not reach the diagnostic endpoint."
      };
    }
  },

  /**
   * Primary orchestration vector for all system-triggered relay events.
   * Configured to support Postmark ServerClient payload structure.
   * Enforces strict 5s timeout for Hop 5 Transmission ACK.
   */
  async processEvent(params: { to: string; event: SystemEvent; context: any }): Promise<{ success: boolean; trace: HopTrace[] }> {
    const { to, event, context } = params;
    const executionId = `exec_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const trace: HopTrace[] = [];

    console.log(`[ORCHESTRATOR] Initiating Trace: ${executionId} for Event: ${event}`);

    try {
      // HOP 0: CONFIG RETRIEVAL
      const postmarkConfig = await firebaseService.getPostmarkConfig();
      const fromEmail = postmarkConfig?.fromEmail || "command@priori-ai.com";
      trace.push({ step: 0, name: 'Config resolution', status: 'PASS', timestamp: Date.now(), details: `Sender ID: ${fromEmail}` });

      // HOP 1: ASSOCIATION RESOLUTION
      const assoc = await firebaseService.getAssociationByEvent(event);
      if (!assoc) throw new Error(`Routing Protocol Failed: No association bound to event [${event}]`);
      trace.push({ step: 1, name: 'Association binding', status: 'PASS', timestamp: Date.now(), details: `Event [${event}] mapped to Task [${assoc.emailTask}]` });

      // HOP 2: PAYLOAD SHARD RETRIEVAL
      const template = await firebaseService.getTemplate(assoc.emailTask);
      if (!template) throw new Error(`Payload Shard Missing: Template [${assoc.emailTask}] not found in registry.`);
      trace.push({ step: 2, name: 'Payload retrieval', status: 'PASS', timestamp: Date.now(), details: `Shard [${template.name}] loaded successfully.` });

      // HOP 3: TACTICAL VARIABLE INJECTION
      const rendered = this.renderTemplate(template, { ...context, executionId });
      trace.push({ step: 3, name: 'Template rendering', status: 'PASS', timestamp: Date.now(), details: 'Injected tactical variables into payload shard.' });

      // HOP 4: AUDIT REGISTRY (Pre-emptive push)
      trace.push({ step: 4, name: 'Audit registry', status: 'PASS', timestamp: Date.now(), details: 'Provisioning execution audit log in Firestore cluster.' });

      const log: EmailLog = {
        id: executionId,
        channel: 'EMAIL',
        triggerType: event,
        recipient: to,
        status: 'PENDING',
        timestamp: Date.now(),
        subject: rendered.subject,
        metadata: { systemEvent: event, trace: [...trace] }
      };
      await firebaseService.logEmail(log);

      // HOP 5: TRANSMISSION ACK (Strict 5s Window)
      const hop5Index = trace.length;
      const hop5Start = Date.now();
      const msgStream = context.messageStream || 'outbound';

      // Initialize Hop 5 as PENDING with explicit Payload Info
      trace.push({
        step: 5,
        name: 'Transmission ACK',
        status: 'PENDING',
        timestamp: hop5Start,
        details: `Dispatching to [${to}] via [${msgStream}]... (Window: 5000ms)`
      });

      // Postmark expects PascalCase keys: From, To, Subject, HtmlBody, TextBody, MessageStream
      const payload: any = {
        From: fromEmail,
        To: to,
        Subject: rendered.subject,
        HtmlBody: rendered.body,
        TextBody: (rendered.body || "").replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>?/gm, '').trim() || "See HTML content.",
        MessageStream: msgStream,
        trackingId: executionId
      };

      if (context.updateTokenMeta) {
        payload.updateTokenMeta = context.updateTokenMeta;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // Strict 5s timeout

      let responseJson: any;
      let responseStatus = 0;

      try {
        console.log(`[ORCHESTRATOR] Hop 5: Sending to Relay...`);
        const authToken = await getAuthToken();
        const response = await fetch(RELAY_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
          cache: 'no-store'
        });

        clearTimeout(timeoutId);
        responseStatus = response.status;

        const responseText = await response.text();
        try {
          responseJson = JSON.parse(responseText);
        } catch (e) {
          // Detect HTML response (usually a 404 or 500 error page from the web server)
          if (responseText.trim().startsWith('<') || responseText.includes('404')) {
            throw new Error(`Relay Endpoint Error: Server returned HTML (likely 404/500). If testing locally, ensure backend is running on 8080.`);
          }
          throw new Error(`Invalid JSON from Relay: ${responseText.substring(0, 100)}`);
        }

        // Ingest Server-Side Trace Evidence for Observability
        if (responseJson.serverTrace && Array.isArray(responseJson.serverTrace)) {
          responseJson.serverTrace.forEach((st: any, idx: number) => {
            const timingInfo = st.duration ? ` [${st.duration}ms]` : '';
            trace.push({
              step: Number(`5.${idx + 1}`),
              name: `Relay: ${st.step}`,
              status: st.status === 'PASS' ? 'PASS' : 'FAIL',
              timestamp: st.timestamp || Date.now(),
              details: `${st.msg}${timingInfo}`
            });
          });
        }

        if (!response.ok) {
          let diagnostic = `Relay Node error (${response.status})`;
          if (responseJson.postmarkCode) {
            diagnostic = `Postmark Rejection [Code ${responseJson.postmarkCode}]: ${responseJson.error || responseJson.details || 'Unknown Postmark Error'}`;
          } else {
            diagnostic = `Relay Error: ${responseJson.details || responseJson.error || responseText}`;
          }
          throw new Error(diagnostic);
        }

        // UPDATE HOP 5 - Success
        trace[hop5Index] = {
          step: 5,
          name: 'Transmission ACK',
          status: 'PASS',
          timestamp: Date.now(),
          details: `ACK Received (200 OK). ID: ${responseJson.MessageID} | Stream: ${msgStream} | Latency: ${Date.now() - hop5Start}ms`
        };

      } catch (e: any) {
        clearTimeout(timeoutId);

        // Handle Timeout vs Error
        let status: 'TIMEOUT' | 'FAIL' = 'FAIL';
        let details = e.message;

        if (e.name === 'AbortError') {
          status = 'TIMEOUT';
          details = 'Gateway Timeout (>5000ms). Provider ACK not received in window.';
        }

        trace[hop5Index] = {
          step: 5,
          name: 'Transmission ACK',
          status: status,
          timestamp: Date.now(),
          details: details
        };

        throw new Error(details); // Re-throw to trigger catch block for final logging
      }

      // HOP 6: VANGUARD VERIFICATION
      trace.push({ step: 6, name: 'Vanguard verification', status: 'PASS', timestamp: Date.now(), details: 'Cycle Complete. Trace finalized.' });

      // Final audit sync - SENT
      await firebaseService.logEmail({
        ...log,
        status: 'SENT',
        messageId: responseJson ? responseJson.MessageID : undefined,
        metadata: { ...log.metadata, trace: [...trace] }
      });

      return { success: true, trace };

    } catch (e: any) {
      console.error(`[ORCHESTRATOR-FAILURE] ${executionId}:`, e.message);

      const failTrace: HopTrace = {
        step: trace.length,
        name: 'Protocol Failure',
        status: 'FAIL',
        timestamp: Date.now(),
        details: e.message
      };

      // Avoid duplicate failure trace if Hop 5 already failed
      if (trace[trace.length - 1]?.status !== 'FAIL' && trace[trace.length - 1]?.status !== 'TIMEOUT') {
        trace.push(failTrace);
      }

      await firebaseService.logEmail({
        id: executionId,
        channel: 'EMAIL',
        triggerType: event,
        recipient: to,
        status: 'FAILED',
        timestamp: Date.now(),
        diagnostic: e.message,
        metadata: { systemEvent: event, trace: [...trace] }
      });

      return { success: false, trace };
    }
  },

  /**
   * Specialized orchestration for accountability partner notifications.
   */
  async notifyPartners(task: Task, event: SystemEvent, userName: string, userEmail: string, subtask?: SubTask): Promise<void> {
    const activePartners = (task.partners || []).filter(p => p.notifyOn.includes(event));
    if (activePartners.length === 0) return;

    for (const partner of activePartners) {
      const pulseLink = `${window.location.origin}/#/partner-action?action=request_pulse&uid=${task.userId}&tid=${task.id}&p=${encodeURIComponent(partner.email)}`;

      await this.processEvent({
        to: partner.email,
        event: event,
        context: {
          userName,
          userEmail,
          taskTitle: task.title,
          taskId: task.id,
          quadrant: task.currentQuadrant,
          subtaskTitle: subtask?.text || 'N/A',
          subtaskStatus: subtask?.completed ? 'COMPLETED' : 'IN_PROGRESS',
          pulseRequestUrl: pulseLink,
          appUrl: window.location.origin,
          timestamp: new Date().toLocaleString(),
          updateTokenMeta: {
            taskId: task.id,
            targetUserId: task.userId,
            requesterEmail: partner.email,
            sourceEvent: event
          }
        }
      });
    }
  },

  /**
   * Primitive but reliable template renderer for variable injection.
   */
  renderTemplate(template: MessageTemplate, context: any): { subject: string; body: string } {
    let subject = template.subject;
    let body = template.body;

    Object.keys(context).forEach(key => {
      const value = context[key] || '';
      // Global replace for handlebars-style variables
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, String(value));
      body = body.replace(regex, String(value));
    });

    return { subject, body };
  }
};
